import type { FieldValues, UseFormReturn } from 'react-hook-form';
import type { z } from 'zod';

import { toast } from 'sonner';

/**
 * Finds the first error field path from a Zod error
 * Returns the field path (e.g., "content.0.questions.1.content") or null if no error found
 */
export function findFirstErrorPathFromZod(zodError: z.ZodError): string | null {
  const firstIssue = zodError.issues[0];
  if (!firstIssue?.path || firstIssue.path.length === 0) {
    return null;
  }
  return firstIssue.path
    .filter((part): part is string | number => typeof part === 'string' || typeof part === 'number')
    .map(String)
    .join('.');
}

/**
 * Extracts error messages from a Zod error
 * Returns only the error messages from issue.message
 */
export function formatZodErrors(zodError: z.ZodError): string[] {
  return zodError.issues.map((issue) => issue.message);
}

export function formErrorToast<TFieldValues extends FieldValues, RZodSchema>(form: UseFormReturn<TFieldValues, unknown, FieldValues>, schema: z.ZodSchema<RZodSchema>, message?: string) {
  const values = form.getValues();
  const result = schema.safeParse(values);
  const zodError = result.error;
  if (zodError) {
    const errorMessages = formatZodErrors(zodError);
    toast.error(message ?? 'Wystąpił błąd podczas zapisywania. Sprawdź poprawność danych.', {
      description: (
        <span style={{ whiteSpace: 'pre-line' }}>
          {errorMessages.map((msg, idx) => (
            <span key={idx}>
              {idx > 0 && '\n'}
              {msg}
            </span>
          ))}
        </span>
      ),
    });
    return result;
  }
}
